import { EProjectType } from '../../../enum/project';
import { IContainerYardImages, IPavedImages, IAirportImagesType, IRailwayImagesType } from '../types';

/**
 * Validate if container yard/parking deck images are ready for PDF generation
 */
export const validateContainerYardImages = (images: IContainerYardImages | null, projectType: EProjectType): boolean => {
    if (!images) return false;

    // For parking_deck, chartImg is optional; for container_yard, it's required
    return projectType === EProjectType.parking_deck ? Boolean(images.tireImg1 && images.tireImg2) : Boolean(images.tireImg1 && images.tireImg2 && images.chartImg);
};

/**
 * Validate if unpaved images are ready based on geotextile selection
 */
export const validateUnpavedImages = (images: IContainerYardImages | null, hasGeotextile: boolean): boolean => {
    if (!images) return false;

    return hasGeotextile ? Boolean(images.tireImg1 && images.tireImg2 && images.tireImg3) : Boolean(images.tireImg1 && images.tireImg2);
};

/**
 * Validate if paved images are ready for PDF generation
 */
export const validatePavedImages = (images: IPavedImages | null): boolean => {
    if (!images) {
        return false;
    }

    // Check if we have classic images (tire images)
    const hasClassicImages = Boolean(images.tireImageUnreinforced && images.tireImageReinforced && images.tireImageGeogrid);

    // Check if we have at least one set of dynamic design images
    const hasSurfaceDesignImages = Boolean(images.surfaceDesignImages && (images.surfaceDesignImages.canvas1 || images.surfaceDesignImages.canvas2 || images.surfaceDesignImages.canvas3));
    const hasBaseDesignImages = Boolean(images.baseDesignImages && (images.baseDesignImages.canvas1 || images.baseDesignImages.canvas2 || images.baseDesignImages.canvas3));
    const hasSubbaseDesignImages = Boolean(images.subbaseDesignImages && (images.subbaseDesignImages.canvas1 || images.subbaseDesignImages.canvas2 || images.subbaseDesignImages.canvas3));

    const hasDynamicImages = hasSurfaceDesignImages || hasBaseDesignImages || hasSubbaseDesignImages;

    // PDF is valid if we have either classic images OR dynamic images
    return hasClassicImages || hasDynamicImages;
};

/**
 * Validate if airport images are ready for PDF generation
 */
export const validateAirportImages = (images: IAirportImagesType | null): boolean => {
    if (!images) return false;

    // Check if we have classic images (tire images)
    const hasClassicImages = Boolean(images.tireImageUnstabilized && images.tireImageIncreasedPassages && images.tireImageGeogrid);

    // Check if we have at least one set of dynamic design images
    const hasSurfaceDesignImages = Boolean(images.surfaceDesignImages && (images.surfaceDesignImages.canvas1 || images.surfaceDesignImages.canvas2 || images.surfaceDesignImages.canvas3));
    const hasBaseDesignImages = Boolean(images.baseDesignImages && (images.baseDesignImages.canvas1 || images.baseDesignImages.canvas2 || images.baseDesignImages.canvas3));
    const hasSubbaseDesignImages = Boolean(images.subbaseDesignImages && (images.subbaseDesignImages.canvas1 || images.subbaseDesignImages.canvas2 || images.subbaseDesignImages.canvas3));

    const hasDynamicImages = hasSurfaceDesignImages || hasBaseDesignImages || hasSubbaseDesignImages;

    // PDF is valid if we have either classic images OR dynamic images
    return hasClassicImages || hasDynamicImages;
};

/**
 * Validate if railway images are ready for PDF generation
 */
export const validateRailwayImages = (images: IRailwayImagesType | null): boolean => {
    if (!images) return false;

    return Boolean(images.tireImageUnreinforced && images.tireImageReinforced);
};

/**
 * Generic image validation based on project type
 */
export const validateImagesByProjectType = (projectType: EProjectType, images: unknown, hasGeotextile?: boolean): boolean => {
    switch (projectType) {
        case EProjectType.container_yard:
        case EProjectType.parking_deck:
            return validateContainerYardImages(images as IContainerYardImages, projectType);

        case EProjectType.unpaved:
            return validateUnpavedImages(images as IContainerYardImages, hasGeotextile || false);

        case EProjectType.paved:
            return validatePavedImages(images as IPavedImages);

        case EProjectType.airport:
            return validateAirportImages(images as IAirportImagesType);

        case EProjectType.railways:
            return validateRailwayImages(images as IRailwayImagesType);

        default:
            return false;
    }
};

/**
 * PDF validators object for backward compatibility
 */
export const pdfValidators = {
    validateContainerYardImages,
    validateUnpavedImages,
    validatePavedImages,
    validateAirportImages,
    validateRailwayImages,
    validateImagesByProjectType,
};
